import { Box } from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import {
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Bank, CreditAgreement } from '@finley-technologies/finley-types';

import { BorrowingBaseSummaryAccordion } from './BorrowingBaseSummaryAccordion';
import { BorrowingBaseSummaryAccordionRow } from './BorrowingBaseSummaryAccordionRow';
import { BorrowingBaseSummaryAccordionSubRow } from './BorrowingBaseSummaryAccordionSubRow';
import { BorrowingBaseSummarySectionHeader } from './BorrowingBaseSummarySectionHeader';
import {
  getContributingBankAccountAdvanceRates,
  getBankAccountCashAdvanceRateSubRowLabel,
  getColumnValuesForBorrowingBaseReportField,
  BorrowingBaseSummaryInput,
} from './utils';

export const BorrowingBaseSummaryCash: React.FC<{
  borrowingBaseReports: BorrowingBaseSummaryInput;
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  showComparisonAndDifferenceColumns: boolean;
}> = ({
  borrowingBaseReports,
  creditAgreement,
  showComparisonAndDifferenceColumns,
}) => {
  const minuendBankAccountCashBalances =
    borrowingBaseReports.minuendBorrowingBaseReport.bankAccountCashBalances;

  const {
    facilityMechanics: {
      parties: { borrowers },
    },
  } = creditAgreement;

  const bankAccountAdvanceRates = getContributingBankAccountAdvanceRates({
    borrowers,
  });

  const collectionsAccountTypeExists = _.some(
    minuendBankAccountCashBalances,
    ({ accountType }) => accountType === Bank.BorrowerAccountType.Collections,
  );
  const contributingBankAccountCashBalances = _.filter(
    minuendBankAccountCashBalances,
    ({ accountType }) =>
      commonTypeguards.isBorrowingBaseContributionAccountType(accountType),
  );

  return (
    <Box marginTop="32px">
      <BorrowingBaseSummarySectionHeader headerLabel="Cash" />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Total Pre-advance Cash Contribution"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'totalPreAdvanceCashContribution',
            })}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            fieldName="totalPreAdvanceCashContribution"
          />
        )}
        accordionPanel={
          <>
            {/* if collections account doesn't exist, render the borrowing base reserve amount on its own */}
            {/* since it will not get rendered with the collections account balance below */}
            {!collectionsAccountTypeExists && (
              <BorrowingBaseSummaryAccordionSubRow
                label="Borrowing Base Reserve Amount"
                columnValues={getColumnValuesForBorrowingBaseReportField({
                  borrowingBaseReports,
                  fieldName: 'borrowingBaseReserveAmount',
                })}
                valueType="currency"
                showComparisonAndDifferenceColumns={
                  showComparisonAndDifferenceColumns
                }
                fieldName="borrowingBaseReserveAmount"
              />
            )}
            {contributingBankAccountCashBalances &&
              _.map(
                contributingBankAccountCashBalances,
                ({ accountType, balance }) => {
                  const balanceForAccountTypeSubtrahend = _.find(
                    borrowingBaseReports.subtrahendBorrowingBaseReport
                      ?.bankAccountCashBalances,
                    (cashBalance) => cashBalance.accountType === accountType,
                  )?.balance;

                  const balanceForAccountTypeReference = _.find(
                    borrowingBaseReports.referenceBorrowingBaseReport
                      ?.bankAccountCashBalances,
                    (cashBalance) => cashBalance.accountType === accountType,
                  )?.balance;

                  // always render borrowing base reserve right under collections, since reserve only applies to collections account balance
                  // if collections account doesn't exist, it will be rendered at the very beginning of this section above
                  if (accountType === Bank.BorrowerAccountType.Collections) {
                    return (
                      <React.Fragment key={`balance-${accountType}`}>
                        <BorrowingBaseSummaryAccordionSubRow
                          label="Pre-reserve Collections Cash Balance"
                          columnValues={{
                            minuendValue: balance,
                            subtrahendValue: balanceForAccountTypeSubtrahend,
                            referenceValue: balanceForAccountTypeReference,
                          }}
                          valueType="currency"
                          showComparisonAndDifferenceColumns={
                            showComparisonAndDifferenceColumns
                          }
                          fieldName="totalCollectionsAccountCash"
                        />
                        <BorrowingBaseSummaryAccordionSubRow
                          label="Borrowing Base Reserve Amount"
                          columnValues={getColumnValuesForBorrowingBaseReportField(
                            {
                              borrowingBaseReports,
                              fieldName: 'borrowingBaseReserveAmount',
                            },
                          )}
                          valueType="currency"
                          showComparisonAndDifferenceColumns={
                            showComparisonAndDifferenceColumns
                          }
                          fieldName="borrowingBaseReserveAmount"
                        />
                      </React.Fragment>
                    );
                  }
                  return (
                    <BorrowingBaseSummaryAccordionSubRow
                      key={`balance-${accountType}`}
                      label={`${commonUtils.toTitleCase(
                        accountType,
                      )} Cash Balance`}
                      columnValues={{
                        minuendValue: balance,
                        subtrahendValue: balanceForAccountTypeSubtrahend,
                        referenceValue: balanceForAccountTypeReference,
                      }}
                      valueType="currency"
                      showComparisonAndDifferenceColumns={
                        showComparisonAndDifferenceColumns
                      }
                      fieldName="cashBalanceForAccount"
                    />
                  );
                },
              )}
          </>
        }
      />

      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Effective Cash Advance Rate"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'effectiveCashAdvanceRate',
            })}
            valueType="percentage"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            fieldName="effectiveCashAdvanceRate"
          />
        )}
        accordionPanel={
          <Box>
            {_.map(
              bankAccountAdvanceRates,
              ({
                accountType,
                cashAdvanceRate: bankAccountCashAdvanceRate,
              }) => {
                return (
                  <BorrowingBaseSummaryAccordionSubRow
                    key={`sub-row-cash-advance-rate-${accountType}`}
                    label={getBankAccountCashAdvanceRateSubRowLabel(
                      accountType,
                    )}
                    columnValues={{
                      minuendValue: bankAccountCashAdvanceRate,
                      subtrahendValue: undefined,
                      referenceValue: undefined,
                    }}
                    valueType="string"
                    showComparisonAndDifferenceColumns={
                      showComparisonAndDifferenceColumns
                    }
                    fieldName="cashAdvanceRate"
                  />
                );
              },
            )}
          </Box>
        }
      />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Total Cash Borrowing Base Amount"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'totalCashBorrowingBaseAmount',
            })}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            fieldName="totalCashBorrowingBaseAmount"
          />
        )}
      />
    </Box>
  );
};
